/**
 * PR43: Skill Registry — 12 skills, all preview-only, no live execution
 * @version 0.1.10
 * @safety dryRunOnly=true, realExecutionEnabled=false for all entries
 */
export const SKILL_REGISTRY = [
    {
        id: "web-search",
        name: "Web Search",
        category: "research",
        version: "0.1.0",
        description: "Preview-only web search skill — no real network calls",
        relatedAgentIds: ["researcher-agent"],
        dryRunOnly: true, previewOnly: true, realExecutionEnabled: false,
        requireOwnerApproval: true, auditEnabled: true,
        networkAllowed: false, productionDbAllowed: false,
    },
    {
        id: "content-writing",
        name: "Content Writing",
        category: "writing",
        version: "0.1.0",
        description: "Preview-only content writing skill",
        relatedAgentIds: ["writer-agent"],
        dryRunOnly: true, previewOnly: true, realExecutionEnabled: false,
        requireOwnerApproval: true, auditEnabled: true,
        networkAllowed: false, productionDbAllowed: false,
    },
    {
        id: "code-generation",
        name: "Code Generation",
        category: "coding",
        version: "0.1.0",
        description: "Preview-only code generation skill",
        relatedAgentIds: ["coder-agent"],
        dryRunOnly: true, previewOnly: true, realExecutionEnabled: false,
        requireOwnerApproval: true, auditEnabled: true,
        networkAllowed: false, productionDbAllowed: false,
    },
    {
        id: "data-analysis",
        name: "Data Analysis",
        category: "analysis",
        version: "0.1.0",
        description: "Preview-only data analysis skill",
        relatedAgentIds: ["analyst-agent"],
        dryRunOnly: true, previewOnly: true, realExecutionEnabled: false,
        requireOwnerApproval: true, auditEnabled: true,
        networkAllowed: false, productionDbAllowed: false,
    },
    {
        id: "task-planning",
        name: "Task Planning",
        category: "planning",
        version: "0.1.0",
        description: "Preview-only task planning skill",
        relatedAgentIds: ["planner-agent", "orchestrator-agent"],
        dryRunOnly: true, previewOnly: true, realExecutionEnabled: false,
        requireOwnerApproval: true, auditEnabled: true,
        networkAllowed: false, productionDbAllowed: false,
    },
    {
        id: "test-generation",
        name: "Test Generation",
        category: "testing",
        version: "0.1.0",
        description: "Preview-only test generation skill",
        relatedAgentIds: ["tester-agent"],
        dryRunOnly: true, previewOnly: true, realExecutionEnabled: false,
        requireOwnerApproval: true, auditEnabled: true,
        networkAllowed: false, productionDbAllowed: false,
    },
    {
        id: "ui-design",
        name: "UI Design",
        category: "design",
        version: "0.1.0",
        description: "Preview-only UI design skill",
        relatedAgentIds: ["designer-agent"],
        dryRunOnly: true, previewOnly: true, realExecutionEnabled: false,
        requireOwnerApproval: true, auditEnabled: true,
        networkAllowed: false, productionDbAllowed: false,
    },
    {
        id: "tutoring",
        name: "Tutoring",
        category: "teaching",
        version: "0.1.0",
        description: "Preview-only tutoring skill",
        relatedAgentIds: ["teacher-agent"],
        dryRunOnly: true, previewOnly: true, realExecutionEnabled: false,
        requireOwnerApproval: true, auditEnabled: true,
        networkAllowed: false, productionDbAllowed: false,
    },
    {
        id: "code-review",
        name: "Code Review",
        category: "review",
        version: "0.1.0",
        description: "Preview-only code review skill",
        relatedAgentIds: ["code-reviewer"],
        dryRunOnly: true, previewOnly: true, realExecutionEnabled: false,
        requireOwnerApproval: true, auditEnabled: true,
        networkAllowed: false, productionDbAllowed: false,
    },
    {
        id: "security-review",
        name: "Security Review",
        category: "security",
        version: "0.1.0",
        description: "Preview-only security review skill",
        relatedAgentIds: ["code-reviewer"],
        dryRunOnly: true, previewOnly: true, realExecutionEnabled: false,
        requireOwnerApproval: true, auditEnabled: true,
        networkAllowed: false, productionDbAllowed: false,
    },
    {
        id: "coding-standards",
        name: "Coding Standards",
        category: "documentation",
        version: "0.1.0",
        description: "Preview-only coding standards skill",
        relatedAgentIds: ["coder-agent", "code-reviewer"],
        dryRunOnly: true, previewOnly: true, realExecutionEnabled: false,
        requireOwnerApproval: true, auditEnabled: true,
        networkAllowed: false, productionDbAllowed: false,
    },
    {
        id: "general-assistance",
        name: "General Assistance",
        category: "general",
        version: "0.1.0",
        description: "Preview-only general assistance skill",
        relatedAgentIds: ["assistant-agent"],
        dryRunOnly: true, previewOnly: true, realExecutionEnabled: false,
        requireOwnerApproval: true, auditEnabled: true,
        networkAllowed: false, productionDbAllowed: false,
    },
];
export function getSkillById(id) {
    return SKILL_REGISTRY.find(s => s.id === id);
}
export function listSkillIds() {
    return SKILL_REGISTRY.map(s => s.id);
}
export function getSkillsForAgent(agentId) {
    return SKILL_REGISTRY.filter(s => s.relatedAgentIds.includes(agentId));
}
export function assertNoLiveSkills() {
    for (const skill of SKILL_REGISTRY) {
        if (skill.realExecutionEnabled !== false)
            throw new Error(`SAFETY VIOLATION: Skill ${skill.id} has realExecutionEnabled=true`);
        if (skill.dryRunOnly !== true)
            throw new Error(`SAFETY VIOLATION: Skill ${skill.id} has dryRunOnly=false`);
    }
}
export function validateSkillRegistry() {
    const errors = [];
    const ids = new Set();
    for (const skill of SKILL_REGISTRY) {
        if (ids.has(skill.id))
            errors.push(`Duplicate skill id: ${skill.id}`);
        ids.add(skill.id);
        if (!skill.id)
            errors.push("Skill missing id");
        if (!skill.name)
            errors.push(`Skill ${skill.id} missing name`);
        if (!skill.category)
            errors.push(`Skill ${skill.id} missing category`);
        if (!skill.version)
            errors.push(`Skill ${skill.id} missing version`);
        if (!skill.description)
            errors.push(`Skill ${skill.id} missing description`);
        if (!Array.isArray(skill.relatedAgentIds))
            errors.push(`Skill ${skill.id} missing relatedAgentIds`);
        if (skill.realExecutionEnabled !== false)
            errors.push(`Skill ${skill.id}: realExecutionEnabled must be false`);
        if (skill.dryRunOnly !== true)
            errors.push(`Skill ${skill.id}: dryRunOnly must be true`);
        if (skill.networkAllowed !== false)
            errors.push(`Skill ${skill.id}: networkAllowed must be false`);
        if (skill.productionDbAllowed !== false)
            errors.push(`Skill ${skill.id}: productionDbAllowed must be false`);
    }
    return { valid: errors.length === 0, errors };
}
//# sourceMappingURL=registry.js.map